<script lang="ts">
  /**
   * "What changed today" — item 12.
   *
   * One reverse-chronological list across the whole estate. This is the screen
   * you actually open when something feels off, and every one of these events was
   * already flowing through Fabric before the trail existed — none of them were
   * written anywhere durable.
   *
   * Append-only: `firestore.rules` denies update and delete to every role
   * including owner, and there is deliberately no delete affordance here.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type Entry = {
    id: string;
    at: string | null;
    kind: string;
    actor: { uid: string; email: string | null };
    target: string;
    summary: string;
    detail: Record<string, unknown>;
  };

  /** The discriminated kinds, in the order they matter when scanning. */
  const KINDS = [
    "claims",
    "guest.issue",
    "guest.revoke",
    "session.revoke",
    "portal.enabled",
    "portal.upsert",
    "service.scaling",
    "service.traffic",
    "release.rollback",
    "flag",
    "runbook",
    "drift",
    "digest.mute",
  ];

  const ICON: Record<string, string> = {
    claims: "users",
    "guest.issue": "user",
    "guest.revoke": "user",
    "session.revoke": "shield",
    "portal.enabled": "door",
    "portal.upsert": "door",
    "service.scaling": "bolt",
    "service.traffic": "bolt",
    "release.rollback": "clock",
    flag: "gear",
    runbook: "wrench",
    drift: "shield",
    "digest.mute": "chat",
  };

  let entries = $state<Entry[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let kind = $state<string>("");
  let day = $state<string>("");
  let open = $state<string | null>(null);

  async function load() {
    loading = true;
    try {
      const qs = new URLSearchParams({ limit: "100" });
      if (kind) qs.set("kind", kind);
      if (day) qs.set("day", day);
      entries = (await api.get<{ entries: Entry[] }>(`/audit?${qs}`)).entries;
      error = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not load the trail";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });

  function time(at: string | null): string {
    if (!at) return "—";
    return new Date(at).toLocaleTimeString("en-ZA", { timeZone: "Africa/Johannesburg", hour: "2-digit", minute: "2-digit" });
  }
</script>

<div class="wrap">
  <section class="card">
    <div class="sec">
      <span class="kicker">What changed</span>
      <span class="filters">
        <select bind:value={kind} onchange={load} aria-label="Filter by kind">
          <option value="">every kind</option>
          {#each KINDS as k (k)}<option value={k}>{k}</option>{/each}
        </select>
        <input type="date" bind:value={day} onchange={load} aria-label="Filter by day" />
        {#if kind || day}
          <button class="lnk" onclick={() => { kind = ""; day = ""; void load(); }}>Clear</button>
        {/if}
      </span>
    </div>

    {#if loading}
      <p class="sub pad">Loading…</p>
    {:else if error}
      <p class="sub pad"><Icon name="alert" size={12} /> {error}</p>
    {:else if entries.length === 0}
      <p class="sub pad">
        <Icon name="check" size={12} />
        {kind || day ? "Nothing matches that filter." : "Nothing has changed yet. Every claims edit, switch and rollback lands here."}
      </p>
    {:else}
      {#each entries as e (e.id)}
        <div class="ent">
          <button class="ent__head" onclick={() => (open = open === e.id ? null : e.id)} aria-expanded={open === e.id}>
            <span class="ent__icon"><Icon name={ICON[e.kind] ?? "dot"} size={14} /></span>
            <span class="ent__body">
              <span class="ent__t">{e.summary}</span>
              <span class="sub">
                <span class="num">{e.at ? dateZA(e.at) : "—"} {time(e.at)}</span>
                · {e.actor.email ?? e.actor.uid}
                · <span class="tag">{e.kind}</span>
              </span>
            </span>
            <Icon name={open === e.id ? "chevron-down" : "chevron-right"} size={14} />
          </button>
          {#if open === e.id}
            <pre class="detail">{JSON.stringify(e.detail, null, 2)}</pre>
          {/if}
        </div>
      {/each}
      <p class="sub foot">
        Append-only, kept 400 days. Nothing on this screen can edit or remove an entry — including you.
      </p>
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; flex-wrap: wrap; }
  .filters { display: flex; align-items: center; gap: 8px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 10px 0 0; }
  select, input { background: var(--fill); color: var(--tx); border: 1px solid var(--line);
                  border-radius: var(--r-control); padding: 5px 7px; font-size: 0.75rem; }
  .lnk { font-size: 0.72rem; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }

  .ent { border-bottom: 1px solid var(--line); }
  .ent:last-of-type { border-bottom: none; }
  .ent__head { display: flex; align-items: flex-start; gap: 10px; width: 100%; padding: 9px 2px; text-align: left; }
  .ent__icon { color: var(--mut); padding-top: 1px; }
  .ent__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .ent__t { font-size: 0.7812rem; font-weight: 700; color: var(--tx); }
  .tag { font-size: 0.72rem; font-weight: 800; color: var(--acc); }
  .detail { margin: 0 0 12px; padding: 9px 11px; overflow-x: auto; border-radius: var(--r-control);
            background: var(--fill); box-shadow: inset 0 0 0 1px var(--line);
            font-size: 0.72rem; line-height: 1.5; color: var(--tx2); }
</style>
